"use client";

import Select from "@/component/common/Select";
import { FREQUENCIES } from "@/data/calculator-data";
import { formatPrice, useCurrency } from "@/lib/currency";
import type { Bilingual, Locale } from "@/types/calculator";
import { useLocale } from "next-intl";

function Slider({ label, value, min, max, v, set }: { label: string; value: string; min: number; max: number; v: number; set: (n: number) => void }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{label}</span>
        <span className="font-semibold text-ash dark:text-white">{value}</span>
      </div>
      <input type="range" min={min} max={max} step={1} value={v} onChange={(e) => set(+e.target.value)} className="w-full cursor-pointer accent-accent" />
    </div>
  );
}

type Props = {
  units: { id: string; name: Bilingual; price: number }[];
  unitKey: string; setUnitKey: (k: string) => void; price: number;
  downPct: number; setDownPct: (n: number) => void;
  years: number; setYears: (n: number) => void;
  freq: string; setFreq: (k: string) => void;
};

/** Left controls panel: unit, price, down %, years, frequency. */
export default function CalcControls(p: Props) {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const { code } = useCurrency();
  const nf = new Intl.NumberFormat(ar ? "ar-EG" : "en-US");

  return (
    <div className="space-y-8 border border-black/10 p-6 md:p-8 dark:border-white/10">
      <label className="block">
        <span className="mb-2 block text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{ar ? "اختيار الوحدة" : "Select unit"}</span>
        <Select
          value={p.unitKey}
          onChange={p.setUnitKey}
          options={p.units.map((u) => ({ value: u.id, label: u.name[locale] }))}
        />
      </label>

      <div className="flex items-center justify-between border-b border-black/10 pb-4 dark:border-white/10">
        <span className="text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{ar ? "سعر الوحدة" : "Unit price"}</span>
        <span className="font-bebas text-2xl text-ash dark:text-white">{formatPrice(p.price, code, locale)}</span>
      </div>

      <Slider label={ar ? "نسبة المقدم" : "Down payment"} value={`${nf.format(p.downPct)}%`} min={5} max={50} v={p.downPct} set={p.setDownPct} />
      <Slider label={ar ? "عدد السنين" : "Years"} value={ar ? `${nf.format(p.years)} سنوات` : `${p.years} yrs`} min={1} max={10} v={p.years} set={p.setYears} />

      <div>
        <span className="mb-2 block text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{ar ? "تكرار الدفع" : "Payment frequency"}</span>
        <div className="grid grid-cols-3 border border-black/10 dark:border-white/10">
          {FREQUENCIES.map((f, i) => (
            <button
              key={f.key}
              onClick={() => p.setFreq(f.key)}
              className={`p-3 text-[11px] font-semibold uppercase tracking-wider transition-colors ${i < 2 ? "border-e border-black/10 dark:border-white/10" : ""} ${p.freq === f.key ? "bg-accent text-white" : "text-ash/60 hover:text-ash dark:text-white/60 dark:hover:text-white"}`}
            >
              {f.label[locale]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
